import {
  getCheckoutsByUserId,
  getDirectExecutionsByUserId,
} from '@funkit/api-base'
import {
  type PurifiedCheckoutHistoryItem,
  purifyCheckoutHistoryItem,
} from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'

import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { useDynamicConfig } from '../statsig/useDynamicConfig'

const CHECKOUT_HISTORY_HOOK_LIMIT = 100

/**
 * Poll the current user's checkout + deposit history, mirroring
 * `@funkit/connect`'s `useCheckoutsListenerByUserId`. Fetches both checkouts and
 * direct executions, purifies them, and returns a single list sorted
 * newest-first.
 *
 * **Requirements**: must be used inside `<FunkitProvider>`. `userId` is optional
 * and defaults to the identity configured there — RN's `userId` is a synthetic
 * internal id the host can't easily supply, so the common case is to omit it.
 * The `'0x'` sentinel (no wallet / no externalUserId) disables polling.
 *
 * @returns `{ data, isLoading }` — purified rows, or `undefined` while loading.
 */
export function useCheckoutsListenerByUserId(userId?: string): {
  data?: PurifiedCheckoutHistoryItem[]
  isLoading: boolean
} {
  const { apiKey, userId: identityUserId } = useFunkitIdentity()
  const { listRefresh } = useDynamicConfig(
    'checkoutnotificationsrefreshinterval',
  )

  const resolvedUserId = userId ?? identityUserId
  const effectiveUserId = resolvedUserId === '0x' ? '' : resolvedUserId

  const { data, isLoading } = useQuery({
    enabled: !!effectiveUserId,
    queryKey: ['getPurifiedCheckoutsByUserId', effectiveUserId],
    queryFn: async () => {
      const [checkouts, directExecutions] = await Promise.all([
        getCheckoutsByUserId({
          apiKey,
          logger,
          userId: effectiveUserId,
          limit: CHECKOUT_HISTORY_HOOK_LIMIT,
        }),
        getDirectExecutionsByUserId({
          apiKey,
          logger,
          userId: effectiveUserId,
          limit: CHECKOUT_HISTORY_HOOK_LIMIT,
        }),
      ])

      return [...checkouts, ...directExecutions]
        .map(purifyCheckoutHistoryItem)
        .sort((a, b) => b.createdTimeMs - a.createdTimeMs)
        .slice(0, CHECKOUT_HISTORY_HOOK_LIMIT)
    },
    refetchInterval: listRefresh,
  })

  return { data, isLoading }
}
